import { Check, Crown, Percent, Star } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { Button } from '@/components/button'
import { homeSizeStyles } from '@/features/home/utils/home-size'
import { cn } from '@/utils/styles/cn'

const CheckItem = ({ children }: { children: React.ReactNode }) => (
    <li className="flex items-start gap-3">
        <Check className="mt-0.5 size-5 shrink-0 text-[#2DD282]" strokeWidth={3} />
        <span className="font-semibold text-gray-900">{children}</span>
    </li>
)

export const B2bHero = () => {
    const t = useTranslations()

    return (
        <div className={cn('flex flex-col items-center relative w-full', homeSizeStyles)}>
            <div className="grid w-full grid-cols-1 gap-6 px-4 md:grid-cols-2 md:px-0">
                {/* Option A - Rabatt-Codes */}
                <div className="relative flex flex-col rounded-l border border-gray-200 bg-white p-8 shadow-xs">
                    <div className="mb-4 flex items-center gap-3">
                        <div className="flex size-10 items-center justify-center rounded-full bg-gray-100">
                            <Percent className="size-5 text-gray-700" />
                        </div>
                        <span className="text-sm font-semibold uppercase tracking-widest text-gray-500">
                            {t('for-companies.heroCards.discount.label')}
                        </span>
                    </div>

                    <span className="mb-4 inline-flex w-fit rounded-full bg-[#2DD282]/15 px-3 py-1 text-sm font-semibold text-[#2DD282]">
                        {t('for-companies.heroCards.discount.badge')}
                    </span>

                    <h3 className="mb-3 text-3xl font-extrabold text-gray-900">
                        {t('for-companies.heroCards.discount.title')}
                    </h3>

                    <p className="mb-6 text-gray-500">
                        {t('for-companies.heroCards.discount.description')}
                    </p>

                    <ul className="mb-8 flex flex-col gap-4">
                        <CheckItem>{t('for-companies.heroCards.discount.feature1')}</CheckItem>
                        <CheckItem>{t('for-companies.heroCards.discount.feature2')}</CheckItem>
                        <CheckItem>{t('for-companies.heroCards.discount.feature3')}</CheckItem>
                    </ul>

                    <div className="mt-auto">
                        <div className="mb-6 flex items-baseline gap-1">
                            <span className="text-5xl font-extrabold text-gray-900">
                                {t('for-companies.heroCards.discount.price')}
                            </span>
                            <span className="text-lg text-gray-400">
                                {t('for-companies.heroCards.discount.priceSuffix')}
                            </span>
                        </div>

                        <Button
                            href={{ pattern: '/for-companies/codes', params: undefined }}
                            styleType="unstyled"
                            shape="pill"
                            width="100%"
                            height="54px"
                            className="rounded-m border-2 border-gray-900 text-center text-lg font-semibold text-gray-900 transition-colors hover:bg-gray-900 hover:text-white"
                        >
                            {t('for-companies.heroCards.discount.cta')}
                        </Button>
                    </div>
                </div>

                {/* Premium - Corporate Benefit */}
                <div className="relative flex flex-col rounded-l border border-gray-200 bg-white p-8 shadow-xs">
                    <div className="absolute right-6 top-6">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 px-4 py-1.5 text-sm font-semibold text-white">
                            <Star className="size-4" fill="currentColor" />
                            {t('for-companies.heroCards.benefit.badge')}
                        </span>
                    </div>

                    <div className="mb-6 flex items-center gap-3">
                        <div className="flex size-10 items-center justify-center rounded-full bg-[#2DD282]/15">
                            <Crown className="size-5 text-[#2DD282]" />
                        </div>
                        <span className="text-sm font-semibold uppercase tracking-widest text-gray-500">
                            {t('for-companies.heroCards.benefit.label')}
                        </span>
                    </div>

                    <h3 className="mb-3 text-3xl font-extrabold text-gray-900">
                        {t('for-companies.heroCards.benefit.title')}
                    </h3>

                    <p className="mb-6 text-gray-500">
                        {t('for-companies.heroCards.benefit.description')}
                    </p>

                    <ul className="mb-8 flex flex-col gap-4">
                        <CheckItem>{t('for-companies.heroCards.benefit.feature1')}</CheckItem>
                        <CheckItem>{t('for-companies.heroCards.benefit.feature2')}</CheckItem>
                        <CheckItem>{t('for-companies.heroCards.benefit.feature3')}</CheckItem>
                    </ul>

                    <div className="mt-auto">
                        <div className="mb-6 flex items-baseline gap-1">
                            <span className="text-lg text-gray-400">
                                {t('for-companies.heroCards.benefit.pricePrefix')}
                            </span>
                            <span className="text-5xl font-extrabold text-gray-900">
                                {t('for-companies.heroCards.benefit.price')}
                            </span>
                            <span className="text-lg text-gray-400">
                                {t('for-companies.heroCards.benefit.priceSuffix')}
                            </span>
                        </div>

                        <Button
                            href={{ pattern: '/for-companies/benefit', params: undefined }}
                            styleType="unstyled"
                            shape="pill"
                            width="100%"
                            height="54px"
                            className="rounded-m bg-[#2DD282] text-center text-lg font-semibold text-white transition-colors hover:bg-[#25b871]"
                        >
                            {t('for-companies.heroCards.benefit.cta')}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}
